import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { useKeyboardShortcuts, useToast } from '@/hooks/useKeyboardShortcuts.js';
import {
  ASISTENCIA_BY_ID,
  ASISTENCIA_CREATE,
  ASISTENCIA_UPDATE,
} from '@/graphql/rrhh/asistencias.gql.js';

const METODOS = [
  { label: 'Manual', value: 'Manual' },
  { label: 'QR', value: 'QR' },
  { label: 'GPS', value: 'GPS' },
];

const ESTADOS = [
  { label: 'En curso', value: 'EnCurso' },
  { label: 'Completa', value: 'Completa' },
  { label: 'Ausente', value: 'Ausente' },
  { label: 'Tarde', value: 'Tarde' },
];

const INITIAL_STATE = {
  empleadoId: '',
  sucursalId: '',
  fecha: '',
  checkIn: '',
  checkOut: '',
  metodo: 'Manual',
  lat: '',
  lng: '',
  estado: 'EnCurso',
  notas: '',
};

function validate(form) {
  const errors = {};
  if (!form.empleadoId) {
    errors.empleadoId = 'Indicá el colaborador asociado.';
  }
  if (!form.fecha) {
    errors.fecha = 'Seleccioná la fecha.';
  }
  if (form.checkOut && form.checkIn && form.checkOut < form.checkIn) {
    errors.checkOut = 'El check-out no puede ser anterior al check-in.';
  }
  if (form.lat && Number.isNaN(Number(form.lat))) {
    errors.lat = 'Ingresá una latitud válida.';
  }
  if (form.lng && Number.isNaN(Number(form.lng))) {
    errors.lng = 'Ingresá una longitud válida.';
  }
  return errors;
}

export default function FormAsistencia() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const toast = useToast();
  const formRef = useRef(null);

  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [{ data, fetching }] = useQuery({
    query: ASISTENCIA_BY_ID,
    variables: { id },
    pause: !isEdit,
  });

  const [, createAsistencia] = useMutation(ASISTENCIA_CREATE);
  const [, updateAsistencia] = useMutation(ASISTENCIA_UPDATE);

  useEffect(() => {
    if (data?.asistencia) {
      setForm({
        empleadoId: data.asistencia.empleadoId?.toString() || '',
        sucursalId: data.asistencia.sucursalId?.toString() || '',
        fecha: data.asistencia.fecha || '',
        checkIn: data.asistencia.checkIn || '',
        checkOut: data.asistencia.checkOut || '',
        metodo: data.asistencia.metodo || 'Manual',
        lat: data.asistencia.lat?.toString() || '',
        lng: data.asistencia.lng?.toString() || '',
        estado: data.asistencia.estado || 'EnCurso',
        notas: data.asistencia.notas || '',
      });
    }
  }, [data]);

  useKeyboardShortcuts(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  });

  const pageTitle = useMemo(
    () => (isEdit ? 'Editar asistencia' : 'Registrar asistencia'),
    [isEdit],
  );

  const breadcrumbs = useMemo(() => [
    { label: 'RRHH', href: '/rrhh' },
    { label: 'Asistencias', href: '/rrhh/asistencias' },
    { label: isEdit ? 'Editar' : 'Nueva' },
  ], [isEdit]);

  const handleChange = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      toast.warning('Revisá los campos marcados antes de continuar.');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      empleadoId: Number(form.empleadoId),
      sucursalId: form.sucursalId ? Number(form.sucursalId) : null,
      fecha: form.fecha,
      checkIn: form.checkIn || null,
      checkOut: form.checkOut || null,
      metodo: form.metodo,
      lat: form.lat ? Number(form.lat) : null,
      lng: form.lng ? Number(form.lng) : null,
      estado: form.estado,
      notas: form.notas || null,
    };

    const result = isEdit
      ? await updateAsistencia({ id, input: payload })
      : await createAsistencia({ input: payload });

    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error.message || 'No pudimos guardar la asistencia.');
      return;
    }

    toast.success(isEdit ? 'Actualizamos la asistencia.' : 'Registramos la asistencia.');
    navigate('/rrhh/asistencias');
  };

  return (
    <div className="w-full">
      <PageHeader
        title={pageTitle}
        description="Control de fichadas con soporte para QR y geolocalización."
        breadcrumbs={breadcrumbs}
      />

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="mx-auto grid w-full max-w-[1400px] grid-cols-12 gap-6"
      >
        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos de la asistencia</h2>
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <Field label="Empleado" htmlFor="empleadoId" required error={errors.empleadoId}>
                <input
                  id="empleadoId"
                  value={form.empleadoId}
                  onChange={handleChange('empleadoId')}
                  placeholder="ID del colaborador"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Field label="Sucursal" htmlFor="sucursalId">
                <input
                  id="sucursalId"
                  value={form.sucursalId}
                  onChange={handleChange('sucursalId')}
                  placeholder="ID de la sucursal"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Fecha" htmlFor="fecha" required error={errors.fecha}>
                <input
                  id="fecha"
                  type="date"
                  value={form.fecha}
                  onChange={handleChange('fecha')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Estado" htmlFor="estado">
                <select
                  id="estado"
                  value={form.estado}
                  onChange={handleChange('estado')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Check-in" htmlFor="checkIn">
                <input
                  id="checkIn"
                  type="time"
                  value={form.checkIn}
                  onChange={handleChange('checkIn')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Check-out" htmlFor="checkOut" error={errors.checkOut}>
                <input
                  id="checkOut"
                  type="time"
                  value={form.checkOut}
                  onChange={handleChange('checkOut')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Método" htmlFor="metodo">
                <select
                  id="metodo"
                  value={form.metodo}
                  onChange={handleChange('metodo')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {METODOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Latitud" htmlFor="lat" hint="Opcional" error={errors.lat}>
                <input
                  id="lat"
                  value={form.lat}
                  onChange={handleChange('lat')}
                  placeholder="-34.6037"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 sm:col-span-6">
              <Field label="Longitud" htmlFor="lng" hint="Opcional" error={errors.lng}>
                <input
                  id="lng"
                  value={form.lng}
                  onChange={handleChange('lng')}
                  placeholder="-58.3816"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12">
              <Field label="Notas" htmlFor="notas" hint="Observaciones internas">
                <textarea
                  id="notas"
                  value={form.notas}
                  onChange={handleChange('notas')}
                  rows={4}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
          </div>
          {fetching ? (
            <p className="mt-4 text-sm text-[rgba(var(--muted),0.9)]">Cargando datos de la asistencia...</p>
          ) : null}
        </section>
      </form>

      <StickyActions
        onCancel={() => navigate('/rrhh/asistencias')}
        onSubmit={() => formRef.current?.requestSubmit()}
        isSubmitting={isSubmitting}
        primaryLabel={isEdit ? 'Guardar cambios' : 'Registrar asistencia'}
      />
    </div>
  );
}
